import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'

import { useAddressStore, useLazyFetchMyAddresses, useMutateSaveAddress } from '@entities/Address'
import { useCartStore } from '@entities/Cart'
import { useLazyFetchMyBonusTotal } from '@entities/Loyalty/hooks/useFetchMyBonusTotal'
import { useFetchPingAuth, useLazyFetchPingAuth, User } from '@entities/User'

import { useErrorInfoModal } from '@features/modal'

type AuthContextType = {
  user: User;
  requireRegistration: boolean;
  isAuthorized: boolean;
  loading: boolean;
  refetchUser: () => Promise<User | null>;
  fetchPingUser: () => ReturnType<ReturnType<typeof useLazyFetchPingAuth>['fetch']>;
};

const AuthContext = createContext<AuthContextType | null>(null)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { t: te } = useTranslation('errors')
  const { openErrorInfoModal } = useErrorInfoModal()
  const [ user, setUser ] = useState<User>(null)
  const { data, loading, error, refetch } = useFetchPingAuth()
  const { fetch: fetchPingUser } = useLazyFetchPingAuth()
  const { loadCarts } = useCartStore()
  const { load: loadBonus } = useLazyFetchMyBonusTotal()
  
  // Для миграции гостевого адреса
  const [ fetchMyAddresses ] = useLazyFetchMyAddresses()
  const [ saveAddressMutation ] = useMutateSaveAddress()
  const guestAddressMigrated = useRef(false)

  useEffect(() => {
    if (!error && data) {
      setUser(data.pingAuth ?? null)
    }
  }, [ data, error ])

  const refetchUser = async () => {
    try {
      const result = await refetch()
      const updatedUser = result.data?.pingAuth ?? null
      setUser(updatedUser)
      return updatedUser
    }
    catch (e) {
      console.error('Failed to refetch user', e)
      openErrorInfoModal({
        description: te('fetch_user_error'),
      })
      setUser(null)
      return null
    }
  }

  const requireRegistration = user?.requireRegistration ?? true

  const isAuthorized = useMemo(() => {
    return Boolean(user && !requireRegistration)
  }, [ user, requireRegistration ])

  useEffect(() => {
    const handleLoadCarts = async () => {
      if (!isAuthorized) return
      await loadCarts()
    }

    const syncBonus = async () => {
      try {
        await loadBonus()
      }
      catch (e) {
        console.error('Failed to refetch bonuses after login', e)
        openErrorInfoModal({
          description: te('fetch_bonus_error'),
        })
      }
    }
    
    /**
     * Миграция гостевого адреса при авторизации
     * Если у гостя был сохранён адрес (id начинается с guest-),
     * проверяем есть ли такой же адрес в списке адресов пользователя.
     * Если есть — выбираем его, если нет — сохраняем на сервер.
     */
    const migrateGuestAddress = async () => {
      if (guestAddressMigrated.current) return
      
      const { selectedAddress, setSelectedAddress, clearSavedAddresses } = useAddressStore.getState()
      
      // Проверяем что это гостевой адрес
      if (!selectedAddress?.id || !String(selectedAddress.id).startsWith('guest-')) return
      
      guestAddressMigrated.current = true
      
      // Очищаем локальные сохранённые адреса гостя
      clearSavedAddresses()
      
      try {
        // Загружаем адреса пользователя
        const { data } = await fetchMyAddresses({ fetchPolicy: 'network-only' })
        const myAddresses = data?.myAddresses ?? []
        
        // Ищем совпадение по координатам (более точный метод)
        const guestLat = selectedAddress.customerGeoPoint?.latitude
        const guestLng = selectedAddress.customerGeoPoint?.longitude
        
        const matchingAddress = myAddresses.find((addr) => {
          if (!addr) return false
          
          // Сравниваем координаты (если есть)
          if (guestLat && guestLng && addr.customerGeoPoint) {
            const latMatch = Math.abs((addr.customerGeoPoint.latitude ?? 0) - guestLat) < 0.0001
            const lngMatch = Math.abs((addr.customerGeoPoint.longitude ?? 0) - guestLng) < 0.0001
            if (latMatch && lngMatch) return true
          }
          
          // Если координат нет, сравниваем по тексту адреса
          if (selectedAddress.address && addr.address) {
            return addr.address.toLowerCase() === selectedAddress.address.toLowerCase()
          }
          
          return false
        })
        
        if (matchingAddress) {
          // Нашли совпадение — выбираем существующий адрес
          setSelectedAddress({
            id: matchingAddress.id ?? undefined,
            address: matchingAddress.address,
            address_appartments: matchingAddress.address_appartments,
            address_intercom: matchingAddress.address_intercom,
            address_porch: matchingAddress.address_porch,
            address_floor: matchingAddress.address_floor,
            door_delivery: matchingAddress.door_delivery,
            no_door_call: matchingAddress.no_door_call,
            comment: matchingAddress.comment,
            customerGeoPoint: matchingAddress.customerGeoPoint,
            shop_id: matchingAddress.shop_id,
            shop: matchingAddress.shop,
            recipient_phone: matchingAddress.recipient_phone ?? '',
            recipient_name: matchingAddress.recipient_name ?? '',
            recipient_last_name: matchingAddress.recipient_last_name ?? '',
          })
        }
        else {
          // Нет совпадения — сохраняем гостевой адрес на сервер
          const result = await saveAddressMutation({
            variables: {
              addressInfo: {
                address: selectedAddress.address ?? '',
                address_appartments: selectedAddress.address_appartments,
                address_intercom: selectedAddress.address_intercom,
                address_porch: selectedAddress.address_porch,
                address_floor: selectedAddress.address_floor,
                door_delivery: selectedAddress.door_delivery,
                no_door_call: selectedAddress.no_door_call,
                comment: selectedAddress.comment,
                customerGeoPoint: {
                  latitude: selectedAddress.customerGeoPoint?.latitude,
                  longitude: selectedAddress.customerGeoPoint?.longitude,
                },
                recipient_phone: selectedAddress.recipient_phone ?? '',
                recipient_name: selectedAddress.recipient_name ?? '',
                recipient_last_name: selectedAddress.recipient_last_name ?? '',
              },
            },
          })
          
          const savedAddress = result.data?.saveAddress
          if (savedAddress) {
            setSelectedAddress({
              id: savedAddress.id ?? undefined,
              address: savedAddress.address,
              address_appartments: savedAddress.address_appartments,
              address_intercom: savedAddress.address_intercom,
              address_porch: savedAddress.address_porch,
              address_floor: savedAddress.address_floor,
              door_delivery: savedAddress.door_delivery,
              no_door_call: savedAddress.no_door_call,
              comment: savedAddress.comment,
              customerGeoPoint: savedAddress.customerGeoPoint,
              shop_id: savedAddress.shop_id,
              shop: savedAddress.shop,
              recipient_phone: savedAddress.recipient_phone ?? '',
              recipient_name: savedAddress.recipient_name ?? '',
              recipient_last_name: savedAddress.recipient_last_name ?? '',
            })
          }
        }
      }
      catch (e) {
        console.error('Failed to migrate guest address', e)
        // Не показываем ошибку пользователю — это фоновая операция
      }
    }

    if (isAuthorized) {
      handleLoadCarts()
      syncBonus()
      migrateGuestAddress()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isAuthorized ])

  const loadingUser = loading || (data && user === null)

  // Показываем лоадер пока загружается информация о пользователе
  if (loadingUser) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          width: '100vw',
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            border: '3px solid #f3f3f3',
            borderTop: '3px solid var(--primary-color, #333)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{'@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }'}</style>
      </div>
    )
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        requireRegistration,
        loading: !!loadingUser,
        isAuthorized,
        refetchUser,
        fetchPingUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    // В режиме разработки при fast refresh может возникнуть ситуация,
    // когда контекст ещё не готов - возвращаем дефолтные значения
    if (import.meta.env.DEV) {
      console.warn('useAuth called before AuthProvider is ready (HMR). Using default values.')
      return {
        user: null,
        requireRegistration: true,
        isAuthorized: false,
        loading: true,
        refetchUser: async () => null,
        fetchPingUser: async () => ({ data: undefined }),
      } as AuthContextType
    }
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Безопасная версия useAuth для использования в компонентах,
 * которые могут рендериться до инициализации AuthProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuthSafe = () => {
  const context = useContext(AuthContext)
  return context ?? {
    user: null,
    requireRegistration: true,
    isAuthorized: false,
    loading: true,
    refetchUser: async () => null,
    fetchPingUser: async () => ({ data: undefined }),
  }
}
